import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  ApplianceGroup,
  DataRef,
  DataRefsByKey,
  ParameterSettings,
  Slideshow,
  Utensil,
} from '../../../../../../../shared/models/dataref.model';
import { RefDataService } from '../../../../../../../services/ref-data.service';
import { AbstractControl, FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SequenceLite, StepLite } from '../../../../../../../shared/models/recipe/step-lite.model';
import { ApplianceGroupDurationsLite } from '../../../../../../../shared/models/generic/appliance-group-durations-lite.model';
import { RecipeLite } from '../../../../../../../shared/models/recipe/recipe-lite.model';
import { RecipeService } from '../../../../../../../services/recipe/recipe.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface VariantFormGroupModel {
  applianceGroups: FormControl<string[]>;
  defaultApplianceGroup: FormControl<string | null>;
  durations: UntypedFormControl;
  steps: UntypedFormArray;
  kitchenwares: UntypedFormControl;
  nutrition: UntypedFormControl;
  detectedNutrition: UntypedFormControl;
  utensils: UntypedFormControl;
}

export interface CustomStep {
  sequences: SequenceWithOperations[];
}

export interface SequenceWithOperations {
  operations: unknown[];
  applianceGroup: string;
}

@Component({
  selector: 'app-variant-step-instructions',
  templateUrl: './variant-step-instructions.component.html',
  styleUrls: ['./variant-step-instructions.component.scss'],
  standalone: false,
})
export class VariantStepInstructionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() variant?: RecipeLite | null;
  @Input() formVariant?: FormGroup<VariantFormGroupModel>;
  @Input() applianceGroupsRefData?: DataRefsByKey<ApplianceGroup> | null;
  @Input() utensilsRefData?: DataRefsByKey<Utensil> | null;
  @Input() domain = '';
  @Input() market = '';
  @Input() lang = '';
  @Input() steps?: StepLite[];
  @Input() formSteps?: UntypedFormArray;
  stepTypes = [];
  utensilList: DataRef[] = [];
  techniqueList = [];
  programList = [];
  parameterSettingsList: ParameterSettings[] = [];
  psNamesList = [];
  updateTimesInputs = false;
  kitchenwaresList: DataRef[] = [];
  applianceGroupsList: ApplianceGroup[] = [];
  slideshows: Slideshow[] = [];
  formStep: UntypedFormGroup = new UntypedFormGroup({
    selectAllAppliance: new UntypedFormControl(false),
  });
  textareaInputInstruction = '';
  showBlockInputInstruction = true;
  selectedUtensils: string[] = [];
  filteredUtensils: DataRef[] = [];
  initOptions = {
    base_url: '/tinymce',
    height: 150,
    statusbar: false,
    suffix: '.min',
    content_css: [] as string[], // as string[] is a fix for tinymce wrong typing
    content_css_cors: false,
    menubar: false,
    plugins: 'link lists',
    toolbar:
      'undo redo | bold italic underline strikethrough | link | alignleft aligncenter alignright alignjustify' +
      ' | numlist bullist outdent indent | h1 | h2 | h3 | h4 | h5',
  };

  public readonly applianceGroupsList$: BehaviorSubject<ApplianceGroup[]> = new BehaviorSubject<ApplianceGroup[]>([]);
  public readonly utensilsList$: BehaviorSubject<Utensil[]> = new BehaviorSubject<Utensil[]>([]);

  private formGroups: { [stepId: string]: UntypedFormGroup } = {};
  private readonly destroy$: Subject<void> = new Subject();

  constructor(private refDataService: RefDataService) {}

  ngOnInit(): void {
    this.listenToApplianceGroupsCtrlChange();

    this.formVariant
      ?.get('utensils')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((selectedUtensils: string[]) => {
        this.selectedUtensils = selectedUtensils;
        this.updateFilteredUtensils();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.applianceGroupsRefData?.currentValue) {
      this.applianceGroupsList = this.refDataService.translateReference(
        this.refDataService.toArray(changes.applianceGroupsRefData.currentValue),
        this.market,
        this.lang,
      );

      this.applianceGroupsList$.next(this.applianceGroupsList);
    }
    if (changes.utensilsRefData?.currentValue) {
      this.utensilList = this.refDataService.translateReference(
        this.refDataService.toArray(changes.utensilsRefData.currentValue),
        this.market,
        this.lang,
      );
      this.utensilsList$.next(this.utensilList);
    }
    if (changes.domain || changes.market || changes.lang) {
      this.refDataService.getByKey('kitchenwares', false, this.domain, this.market, this.lang).subscribe((kitchenwares) => {
        kitchenwares = this.refDataService.toArray(kitchenwares);
        this.kitchenwaresList = this.refDataService.translateReference(kitchenwares, this.market, this.lang);
      });
      this.refDataService.getByKey('slideshows', false, this.domain, this.market, this.lang).subscribe((slideshows) => {
        slideshows = this.refDataService.toArray(slideshows);
        this.slideshows = this.refDataService.alphabeticOrder(
          this.refDataService.translateReference(slideshows, this.market, this.lang),
          'translatedName',
        );
      });
      this.refDataService.getByKey('stepTypes', false, this.domain, this.market, this.lang).subscribe((steps) => {
        steps = this.refDataService.toArray(steps);
        this.stepTypes = this.refDataService.alphabeticOrder(
          this.refDataService.translateReference(steps, this.market, this.lang),
          'translatedName',
        );
      });
      this.refDataService.getByKey('utensils', false, this.domain, this.market, this.lang).subscribe((utensils) => {
        utensils = this.refDataService.toArray(utensils);
        this.utensilList = this.refDataService.alphabeticOrder(
          this.refDataService.translateReference(utensils, this.market, this.lang),
          'translatedName',
        );
        this.selectedUtensils = this.formVariant?.get('utensils')?.value ?? [];
        this.updateFilteredUtensils();
      });
      this.refDataService.getByKey('techniques', false, this.domain, this.market, this.lang).subscribe((techniques) => {
        techniques = this.refDataService.toArray(techniques);
        this.techniqueList = this.refDataService.alphabeticOrder(
          this.refDataService.translateReference(techniques, this.market, this.lang),
          'translatedName',
        );
      });
      this.refDataService.getByKey('programs', false, this.domain, this.market, this.lang).subscribe((programs) => {
        programs = this.refDataService.toArray(programs);
        this.programList = this.refDataService.alphabeticOrder(
          this.refDataService.translateReference(programs, this.market, this.lang),
          'translatedName',
        );
      });
      this.refDataService.getByKey('parameterSettings', false, this.domain, this.market, this.lang).subscribe((parameterSettings) => {
        this.parameterSettingsList = this.refDataService.toArray(parameterSettings);
      });
      this.refDataService.getByKey('psNames', false, this.domain, this.market, this.lang).subscribe((psNames) => {
        psNames = this.refDataService.toArray(psNames);
        this.psNamesList = this.refDataService.translateReference(psNames, this.market, this.lang);
      });
    }
    if ((changes.steps?.currentValue && !this.formSteps?.length) || (changes.formVariant && this.steps)) {
      const steps: StepLite[] = changes.steps?.currentValue ?? this.steps;

      if (this.formSteps?.length) {
        this.formSteps.controls = [];
        this.formSteps.reset();
      }
      steps.forEach((step) => {
        this.formSteps?.push(this.getFormGroup(step));
      });
    }
  }

  toControl(absCtrl: AbstractControl): UntypedFormControl {
    return absCtrl as UntypedFormControl;
  }

  addOrRemoveSequence(): void {
    this.formVariant?.controls.steps?.setValue(
      this.formVariant?.controls.steps?.value.map((step: CustomStep) => {
        this.formVariant?.controls.applianceGroups?.value.forEach((appGroup: string) => {
          // check and add
          if (!step.sequences?.some((sequence: SequenceWithOperations) => sequence.applianceGroup === appGroup)) {
            step.sequences.push({ applianceGroup: appGroup, operations: [] });
          }
        });
        step.sequences = step.sequences?.filter((sequence: { applianceGroup: string }) =>
          // check and remove
          this.formVariant?.controls.applianceGroups?.value.some((appliance: string) => appliance === sequence.applianceGroup),
        );
        return step;
      }),
    );
  }

  addOrRemoveApplianceTime(): void {
    // add in duration
    this.formVariant?.controls.applianceGroups?.value.forEach((appGroup: string) => {
      // check and add
      if (
        !this.formVariant?.controls.durations?.value?.applianceGroupDurations?.some(
          (appliance: ApplianceGroupDurationsLite) => appliance.applianceGroup === appGroup,
        )
      ) {
        const duration = this.formVariant?.controls.durations.value;
        const totalTime = duration.offApplianceCookingTime + duration.restingTime + duration.prepTime;
        this.formVariant?.controls.durations?.value?.applianceGroupDurations?.push({
          applianceGroup: appGroup,
          cookingTime: 0,
          totalTime,
        });
      }
    });
    // remove from duration
    if (this.formVariant?.controls.durations?.value.applianceGroupDurations) {
      this.formVariant.controls.durations.value.applianceGroupDurations =
        this.formVariant?.controls.durations.value.applianceGroupDurations.filter((appGroup: ApplianceGroupDurationsLite) =>
          this.formVariant?.controls.applianceGroups?.value.some((appliance: string) => appliance === appGroup.applianceGroup),
        );
    }
    this.updateTimesInputs = !this.updateTimesInputs; // update time input sub component
  }

  addLine(step?: StepLite, varApplicationDescription = ''): void {
    const stepNew: StepLite = {
      applicationDescription: varApplicationDescription,
      sequences: [],
      utensils: [],
      kitchenwares: [],
      techniques: [],
      ingredients: [],
      resourceMedias: [],
      type: 'PREPARATION',
      ...step,
    };
    this.formVariant?.controls.applianceGroups?.value.forEach((appGroup: string) => {
      // check and add
      if (!stepNew.sequences.some((appliance: SequenceLite) => appliance.applianceGroup === appGroup)) {
        stepNew.sequences.push({ applianceGroup: appGroup, operations: [] });
      }
    });
    this.formSteps?.push(this.getFormGroup(stepNew));
    this.formSteps?.markAsDirty();
    this.formSteps?.markAllAsTouched();
  }

  blockInputInstruction(stepsTxt: string = ''): void {
    const txtStepsArray = stepsTxt.trim().split('\n\n');
    txtStepsArray.forEach((txtStep) => {
      this.addLine(undefined, txtStep);
    });
    this.showBlockInputInstruction = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateFilteredUtensils(): void {
    this.filteredUtensils = this.utensilList.filter((utensil) => this.selectedUtensils.includes(utensil.key));
  }

  private listenToApplianceGroupsCtrlChange(): void {
    this.formVariant
      ?.get('applianceGroups')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.addOrRemoveApplianceTime();
        this.addOrRemoveSequence();
      });
  }

  private getFormGroup(step?: StepLite): UntypedFormGroup {
    if (step?.identifier && this.formGroups[step.identifier]?.pristine) {
      return this.formGroups[step.identifier];
    }

    const formGroup = RecipeService.buildFormGroupStep(step);

    if (step?.identifier) {
      this.formGroups[step.identifier] = formGroup;
    }

    return formGroup;
  }
}
